import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArchiveBoxArrowDownIcon,
  ArrowPathIcon,
  PencilIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import ExpandableCard from '@/components/shared/ExpandableCard';
import { ConfirmDialog } from '@/components/ui';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useDomainStore } from '@/stores/domainStore';
import {
  deleteDomain,
  getDomain,
  getDomainEventCount,
  setDomainStoriesEnabled,
  updateDomain,
} from '@/services/domainRegistry';
import {
  canConfigureDomain,
  canConfigureDomainCapability,
  capabilityIsEnabled,
  getDomainConfigurationPathState,
} from '@/services/domain/centralModel';
import { getCentralDomainDefinition } from '@/services/domain/centralDefinitions';
import { getDomainRecordingType, isAssessmentDomain } from '@/services/domain/recordingContract';
import { getDomainView } from '../utils/domainHelpers';
import { effectiveListBlocks } from '../utils/listBlocks';
import { DomainCreationForm } from './DomainCreationForm';
import { DomainEditForm } from './DomainEditForm';
import DomainPhaseSettings from '@/features/phases/components/DomainPhaseSettings';

const FIELD_TYPE_LABELS = {
  binary: 'Yes / No',
  boolean: 'Yes / No',
  multiChoice: 'Choice',
  number: 'Number',
  assessment: 'Programme assessments',
  text: 'Text',
  string: 'Short text',
};

const VIEW_LABELS = {
  list: 'Class list',
  search: 'Search',
};

function recordingFields(domain) {
  return (domain.primary ? [domain.primary, ...(domain.followUp || [])] : domain.fields || [])
    .filter((field) => field?.retired !== true && field?.active !== false);
}

function inheritedRecordingBlocks(domain) {
  const definition = getCentralDomainDefinition(domain.governance?.definitionId)
    || getCentralDomainDefinition(domain.governance?.prototypeId)
    || getCentralDomainDefinition(domain.name);
  return effectiveListBlocks({ ...domain, listBlocks: definition?.config?.listBlocks || {} },
    recordingFields(domain).filter((field) => field.type === 'number'));
}

// Build the one requested setting from a confirmed snapshot. Never reconstruct
// sibling preferences from whether their controls are currently enabled.
function recordingChange(domain, change) {
  let updates;
  if (change.kind === 'view') updates = { defaultView: change.value };
  if (change.kind === 'quickIssue') {
    const current = domain.surfaces?.quickIssue;
    const quickIssue = typeof current === 'object' && current
      ? current : { enabled: current !== false, notes: true, attachments: true };
    updates = { surfaces: { ...domain.surfaces, quickIssue: { ...quickIssue, [change.key]: change.value } } };
    if (change.key === 'enabled') {
      const inputSurfaces = new Set(domain.inputSurfaces || []);
      if (change.value) inputSurfaces.add('quick-issue');
      else inputSurfaces.delete('quick-issue');
      updates.inputSurfaces = [...inputSurfaces];
    }
  }
  if (change.kind === 'register') {
    const current = domain.capabilities?.register || {};
    const fields = recordingFields(domain).filter((field) => field.type === 'multiChoice');
    const enabled = change.enabled ?? (domain.surfaces?.classDisplay?.enabled === true && capabilityIsEnabled(domain, 'register'));
    const statusFieldId = change.statusFieldId ?? current.statusFieldId ?? fields[0]?.id ?? '';
    const field = fields.find((item) => item.id === statusFieldId);
    const activeValues = new Set((field?.options || [])
      .filter((option) => option?.retired !== true && option?.active !== false).map((option) => option.value));
    const implicitValue = change.implicitValue ?? current.implicitValue;
    const register = { ...current, enabled, ...(statusFieldId ? { statusFieldId } : {}) };
    if (current.schoolFields?.includes('implicitValue')) {
      register.implicitValue = activeValues.has(implicitValue) ? implicitValue : null;
    }
    const inputSurfaces = new Set(domain.inputSurfaces || []);
    if (enabled) inputSurfaces.add('class-display');
    else inputSurfaces.delete('class-display');
    updates = {
      capabilities: { ...domain.capabilities, register },
      surfaces: { ...domain.surfaces, classDisplay: { ...domain.surfaces?.classDisplay, enabled } },
      inputSurfaces: [...inputSurfaces],
    };
  }
  if (change.kind === 'listBlock') {
    const listBlocks = { ...domain.listBlocks };
    if (change.value === inheritedRecordingBlocks(domain)[change.key]) delete listBlocks[change.key];
    else listBlocks[change.key] = change.value;
    updates = { listBlocks };
  }
  const projected = { ...domain, ...updates };
  if (change.kind === 'listBlock') {
    const configuration = domain.governance?.configuration || {};
    const presentation = configuration.event?.presentation || {};
    const listBlocks = { ...presentation.listBlocks };
    if (change.value === inheritedRecordingBlocks(domain)[change.key]) delete listBlocks[change.key];
    else listBlocks[change.key] = change.value;
    projected.governance = { ...domain.governance, configuration: { ...configuration,
      event: { ...configuration.event, presentation: { ...presentation, listBlocks } },
    } };
  }
  return { updates, projected };
}

function RecordingSettings({ domain: liveDomain, schoolId }) {
  const [acknowledged, setAcknowledged] = useState(null);
  const liveRevision = liveDomain.governance?.configurationRevision;
  const acknowledgedRevision = acknowledged?.domain.governance?.configurationRevision;
  const useAcknowledged = acknowledged && (Number.isSafeInteger(liveRevision) && Number.isSafeInteger(acknowledgedRevision)
    ? acknowledgedRevision >= liveRevision : acknowledged.source === liveDomain);
  const domain = useAcknowledged ? acknowledged.domain : liveDomain;
  const latestDomain = useRef(domain);
  latestDomain.current = domain;
  const [change, setChange] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const busy = status === 'saving' || status === 'loading';
  const controlsLocked = busy || status === 'error';
  const saving = useRef(false);
  const mounted = useRef(true);
  const saveNumber = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const displayed = change ? recordingChange(domain, change).projected : domain;
  const assessmentRecording = isAssessmentDomain(domain);
  const fields = recordingFields(domain);
  const statusFields = fields.filter((field) => field.type === 'multiChoice');
  const numberFields = fields.filter((field) => field.type === 'number');
  const inheritedListBlocks = inheritedRecordingBlocks(domain);
  const configuredListBlocks = displayed.governance?.configuration?.event?.presentation?.listBlocks || {};
  const canEditOverview = canConfigureDomain(domain, 'overview', 'edit', 'defaultView');
  const canEditRegister = canConfigureDomainCapability(domain, 'register')
    && canConfigureDomain(domain, 'event', 'edit', 'presentation.classDisplay.enabled');
  const canEditImplicitValue = canEditRegister
    && domain.capabilities?.register?.schoolFields?.includes('implicitValue');
  const defaultView = getDomainView(displayed);
  const classDisplay = displayed.surfaces?.classDisplay?.enabled === true && capabilityIsEnabled(displayed, 'register');
  const quickIssue = displayed.surfaces?.quickIssue;
  const quickIssueEnabled = typeof quickIssue === 'boolean' ? quickIssue : quickIssue?.enabled !== false;
  const notesEnabled = quickIssue?.notes !== false;
  const attachmentsEnabled = quickIssue?.attachments !== false;
  const statusFieldId = displayed.capabilities?.register?.statusFieldId || statusFields[0]?.id || '';
  const implicitValue = displayed.capabilities?.register?.implicitValue || '';
  const listBlocks = effectiveListBlocks(displayed, numberFields);
  const listBlockOverrides = new Set(Object.keys(configuredListBlocks));

  const persist = async (intended, refresh = false) => {
    if (saving.current) return;
    saving.current = true;
    const attempt = ++saveNumber.current;
    if (intended) setChange(intended);
    setStatus(intended ? 'saving' : 'loading');
    setError(null);
    try {
      const base = refresh ? await getDomain(schoolId, domain.id) : latestDomain.current;
      if (!base) throw new Error('This Domain is no longer available.');
      if (!intended) {
        if (!mounted.current) return;
        setAcknowledged({ domain: base, source: liveDomain });
        setChange(null);
        setStatus('idle');
        return;
      }
      const { updates, projected } = recordingChange(base, intended);
      const expectedConfigurationRevision = base.governance?.configurationRevision;
      const options = Number.isSafeInteger(expectedConfigurationRevision) ? [{ expectedConfigurationRevision }] : [];
      const saved = await updateDomain(schoolId, domain.id, updates, ...options);
      if (!mounted.current) return;
      const configurationRevision = saved?.configurationRevision;
      const confirmed = Number.isSafeInteger(configurationRevision)
        ? { ...projected, governance: { ...projected.governance, configurationRevision } } : projected;
      setAcknowledged({ domain: confirmed, source: liveDomain });
      setChange(null);
      setStatus('saved');
      window.setTimeout(() => {
        if (mounted.current && attempt === saveNumber.current) setStatus('idle');
      }, 1800);
    } catch (saveError) {
      if (!mounted.current) return;
      setError(saveError.message || 'Could not save recording settings.');
      setStatus('error');
    } finally {
      saving.current = false;
    }
  };

  const updateView = (value) => persist({ kind: 'view', value });
  const updateQuickIssue = (key, value) => persist({ kind: 'quickIssue', key, value });
  const updateClassDisplay = (settings) => persist({ kind: 'register', ...settings });
  const updateListBlock = (key, value) => persist({ kind: 'listBlock', key, value });
  const resetListBlock = (key) => updateListBlock(key, inheritedListBlocks[key]);
  const selectedStatusField = statusFields.find((field) => field.id === statusFieldId) || statusFields[0] || null;
  const selectedStatusOptions = (selectedStatusField?.options || [])
    .filter((option) => option?.retired !== true && option?.active !== false);
  const quickIssuePath = (path) => `presentation.quickIssue.${path}`;
  const canEditQuickIssue = (path) => canConfigureDomain(domain, 'event', 'edit', quickIssuePath(path));
  const lockReason = (path, fallback) => getDomainConfigurationPathState(domain, 'event', path)?.reason || fallback;

  return (
    <section className="border-t border-base-200 pt-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">Recording</h3>
        {status === 'saving' && <span className="badge badge-info badge-outline badge-sm">Saving…</span>}
        {status === 'loading' && <span className="badge badge-info badge-outline badge-sm">Loading…</span>}
        {status === 'saved' && <span className="badge badge-success badge-outline badge-sm">Saved</span>}
        {status === 'error' && <span className="badge badge-error badge-outline badge-sm">Save failed</span>}
      </div>
      <p className="mt-1 text-xs text-base-content/55">Choose how staff open and complete this Domain.</p>
      {error && <div className="alert alert-error mt-3 py-2 text-sm" role="alert">
        <span>{error} Your change is still selected.</span>
        <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => persist(change, true)}>Retry save</button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => persist(null, true)}>Use saved settings</button>
      </div>}

      <div className="mt-4 divide-y divide-base-200 rounded-xl border border-base-300">
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium">Default entry</p>
            <p className="text-xs text-base-content/50">Where staff begin when they record an event.</p>
          </div>
          {canEditOverview ? (
            <select
              className="select select-bordered select-sm w-full sm:w-44"
              aria-label="Default recording view"
              value={defaultView}
              disabled={controlsLocked}
              onChange={(event) => updateView(event.target.value)}
            >
              <option value="list">Class list</option>
              <option value="search">Learner search</option>
            </select>
          ) : <span className="text-sm font-medium">{VIEW_LABELS[defaultView] || 'Class list'}</span>}
        </div>

        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium">Quick Issue</p>
            <p className="text-xs text-base-content/50">Let staff record this Domain from the shared Quick Issue flow.</p>
            {!canEditQuickIssue('enabled') && (
              <p className="mt-1 text-xs text-base-content/45">
                {lockReason(quickIssuePath('enabled'), domain.protected
                  ? 'This surface follows the established Domain runtime.'
                  : 'This Domain Standard manages Quick Issue availability.')}
              </p>
            )}
          </div>
          {canEditQuickIssue('enabled') ? (
            <label className="flex cursor-pointer items-center justify-between gap-3 text-sm sm:justify-end">
              <span>{quickIssueEnabled ? 'Enabled' : 'Disabled'}</span>
              <input type="checkbox" className="toggle toggle-primary toggle-sm" aria-label="Enable Quick Issue" checked={quickIssueEnabled} disabled={controlsLocked} onChange={(event) => updateQuickIssue('enabled', event.target.checked)} />
            </label>
          ) : <span className="badge badge-outline badge-sm">{quickIssueEnabled ? 'Enabled' : 'Unavailable'}</span>}
        </div>

        <div className="grid gap-3 p-3 sm:grid-cols-2">
          <p className="text-xs text-base-content/50 sm:col-span-2">
            Optional tools for new entries. Existing details remain editable; questions configured under Event stay available.
          </p>
          {[
            { key: 'notes', label: 'Notes', value: notesEnabled, setter: () => updateQuickIssue('notes', !notesEnabled) },
            { key: 'attachments', label: 'Attachments', value: attachmentsEnabled, setter: () => updateQuickIssue('attachments', !attachmentsEnabled) },
          ].map((setting, index) => {
            const editable = canEditQuickIssue(setting.key);
            return (
              <div key={setting.key} className={`flex flex-col gap-1 ${index ? 'sm:border-l sm:border-base-200 sm:pl-3' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm">{setting.label}</span>
                  {editable ? (
                    <input
                      type="checkbox"
                      className="toggle toggle-primary toggle-sm"
                      aria-label={`Enable ${setting.label.toLowerCase()}`}
                      checked={setting.value}
                      disabled={controlsLocked || !quickIssueEnabled}
                      onChange={setting.setter}
                    />
                  ) : <span className="badge badge-outline badge-sm">{setting.value ? 'Enabled' : 'Unavailable'}</span>}
                </div>
                {!editable && (
                  <span className="text-xs text-base-content/45">
                    {lockReason(quickIssuePath(setting.key), domain.protected
                      ? 'This setting follows the established Domain runtime.'
                      : `This Domain Standard manages ${setting.label.toLowerCase()}.`)}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium">Class display</p>
            <p className="text-xs text-base-content/50">
              {statusFields.length
                ? 'Use a Choice field as a tap-through class register.'
                : canEditRegister
                  ? 'Add a Choice field under Event to use the class display.'
                  : 'This Domain Standard does not use the class display.'}
            </p>
          </div>
          {canEditRegister ? (
            <div className="flex flex-col items-stretch gap-2 sm:items-end">
              <label className="flex cursor-pointer items-center justify-between gap-3 text-sm sm:justify-end">
                <span>{classDisplay ? 'Enabled' : 'Disabled'}</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary toggle-sm"
                  aria-label="Enable class display"
                  checked={classDisplay}
                  disabled={controlsLocked || !statusFields.length}
                  onChange={(event) => updateClassDisplay({ enabled: event.target.checked })}
                />
              </label>
              {classDisplay && statusFields.length > 1 && (
                <select
                  className="select select-bordered select-sm w-full sm:w-44"
                  aria-label="Class display field"
                  value={statusFieldId}
                  disabled={controlsLocked}
                  onChange={(event) => updateClassDisplay({ statusFieldId: event.target.value })}
                >
                  {statusFields.map((field) => <option key={field.id} value={field.id}>{field.label}</option>)}
                </select>
              )}
              {classDisplay && canEditImplicitValue && selectedStatusOptions.length > 0 && (
                <label className="flex flex-col gap-1 text-xs text-base-content/60 sm:items-end">
                  <span>Unanswered when finished</span>
                  <select
                    className="select select-bordered select-sm w-full sm:w-52"
                    aria-label="Unanswered learners when the class display register is finished"
                    value={implicitValue}
                    disabled={controlsLocked}
                    onChange={(event) => updateClassDisplay({ implicitValue: event.target.value })}
                  >
                    <option value="">Keep unanswered</option>
                    {selectedStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        Count as {option.label || option.value}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          ) : (
            <span className={`badge badge-outline badge-sm ${classDisplay ? 'badge-success' : ''}`}>
              {classDisplay ? 'Enabled' : 'Unavailable'}
            </span>
          )}
        </div>

        {!assessmentRecording && numberFields.length > 0 && (
          <div className="space-y-3 p-3">
            <div>
              <p className="text-sm font-medium">Number display</p>
              <p className="text-xs text-base-content/50">
                Choose the score context shown with numeric entry. These settings never apply to Choice, Yes / No or text fields.
              </p>
            </div>
            {[
              {
                key: 'outOf',
                title: 'Maximum score',
                description: 'Show what each score is out of when a maximum is available.',
                minimum: 1,
              },
              {
                key: 'average',
                title: 'Class average',
                description: 'Show the average for recorded numeric values.',
                minimum: 1,
              },
              {
                key: 'weighting',
                title: 'Weighting',
                description: 'Show the total from the contributions configured under Event → Combined total.',
                minimum: 2,
              },
            ].filter((setting) => numberFields.length >= setting.minimum).map((setting) => {
              const path = `presentation.listBlocks.${setting.key}`;
              const editable = canConfigureDomain(domain, 'event', 'edit', path);
              const overridden = listBlockOverrides.has(setting.key);
              return (
                <div key={setting.key} className="flex flex-col gap-2 rounded-lg bg-base-200/40 p-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium">{setting.title}</p>
                      {editable && overridden
                        ? <span className="badge badge-primary badge-outline badge-sm">School override</span>
                        : editable
                          ? <span className="badge badge-ghost badge-sm">Swan default</span>
                          : <span className="badge badge-ghost badge-sm">Locked</span>}
                    </div>
                    <p className="mt-0.5 text-xs text-base-content/50">{setting.description}</p>
                    {!editable && (
                      <p className="mt-1 text-xs text-base-content/45">
                        {lockReason(path, 'This Domain Standard manages the numeric display.')}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-end">
                    {editable && overridden && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs gap-1"
                        onClick={() => resetListBlock(setting.key)}
                        disabled={controlsLocked}
                        aria-label={`Reset ${setting.title.toLowerCase()} to Swan default`}
                      >
                        <ArrowPathIcon className="h-3.5 w-3.5" /> Reset
                      </button>
                    )}
                    <label className={`flex items-center gap-2 text-sm ${editable ? 'cursor-pointer' : ''}`}>
                      <span>{listBlocks[setting.key] ? 'Shown' : 'Hidden'}</span>
                      <input
                        type="checkbox"
                        className="toggle toggle-primary toggle-sm"
                        checked={listBlocks[setting.key] === true}
                        disabled={!editable || controlsLocked}
                        onChange={(event) => updateListBlock(setting.key, event.target.checked)}
                        aria-label={`Show ${setting.title.toLowerCase()}`}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </section>
  );
}

function DomainBody({ domain, schoolId, onArchived, onCreated }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveError, setArchiveError] = useState(null);
  const [busyArchiveId, setBusyArchiveId] = useState(null);
  const [storiesOn, setStoriesOn] = useState(domain.storiesEnabled === true);
  const [storiesSaving, setStoriesSaving] = useState(false);
  const [storiesStatus, setStoriesStatus] = useState('idle');
  const [storiesError, setStoriesError] = useState(null);
  const [active, setActive] = useState(domain.active !== false);
  const [availabilityStatus, setAvailabilityStatus] = useState('idle');
  const { domains, setDomains } = useDomainStore();
  const { user } = useAuth();
  const storiesAvailable = capabilityIsEnabled(domain, 'stories');
  const recordingAvailable = capabilityIsEnabled(domain, 'recording');
  const storiesConfigurable = canConfigureDomainCapability(domain, 'stories');
  const canReactivate = canConfigureDomain(domain, 'overview', 'edit', 'active');
  const canEditIdentity = ['label', 'description', 'scope', 'terminology']
    .some((path) => canConfigureDomain(domain, 'overview', 'edit', path));
  const canArchive = canReactivate && domain.runtime?.alwaysOn !== true;

  const toggleStories = async () => {
    if (storiesSaving) return;
    const next = !storiesOn;
    setStoriesOn(next);
    setStoriesSaving(true);
    setStoriesStatus('saving');
    setStoriesError(null);
    try {
      await setDomainStoriesEnabled(schoolId, domain.id, next);
      setDomains(
        domains.map((candidate) => (
          candidate.id === domain.id ? { ...candidate, storiesEnabled: next } : candidate
        )),
        schoolId,
      );
      setStoriesStatus('saved');
      window.setTimeout(() => setStoriesStatus('idle'), 1800);
    } catch (error) {
      console.error('[DomainSettingsCard] toggle stories failed', error);
      setStoriesOn(!next);
      setStoriesError(error.message || 'Could not save Stories.');
      setStoriesStatus('error');
    } finally {
      setStoriesSaving(false);
    }
  };

  const openArchive = async (entity, isSubDomain) => {
    setBusyArchiveId(entity.id);
    setArchiveError(null);
    const count = await getDomainEventCount(schoolId, entity).catch(() => 0);
    setBusyArchiveId(null);
    setArchiveTarget({ entity, count, isSubDomain });
  };

  const archive = async () => {
    if (!archiveTarget) return;
    const { entity, isSubDomain } = archiveTarget;
    setBusyArchiveId(entity.id);
    setArchiveError(null);
    try {
      await deleteDomain(schoolId, entity.id);
      setArchiveTarget(null);
      if (!isSubDomain) onArchived?.(entity.id);
    } catch (error) {
      setArchiveError(error.message || 'Failed to archive this Domain.');
    } finally {
      setBusyArchiveId(null);
    }
  };

  const reactivate = async () => {
    setAvailabilityStatus('saving');
    setArchiveError(null);
    try {
      await updateDomain(schoolId, domain.id, { active: true });
      setActive(true);
      setAvailabilityStatus('saved');
      window.setTimeout(() => setAvailabilityStatus('idle'), 1800);
    } catch (error) {
      setArchiveError(error.message || 'Could not reactivate this Domain.');
      setAvailabilityStatus('error');
    }
  };

  if (isEditing) {
    return (
      <DomainEditForm
        domain={domain}
        schoolId={schoolId}
        onSuccess={() => setIsEditing(false)}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  const primaryType = getDomainRecordingType(domain);
  const scope = domain.scope?.grades || [];

  return (
    <div className="space-y-6">
      {archiveError && <div className="alert alert-error py-2 text-sm">{archiveError}</div>}

      <section>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold">About this Domain</h3>
              <span className={`badge badge-outline badge-sm ${active ? 'badge-success' : 'badge-ghost'}`}>
                {active ? 'Active' : 'Inactive'}
              </span>
              {availabilityStatus === 'saving' && <span className="badge badge-info badge-outline badge-sm">Saving…</span>}
              {availabilityStatus === 'saved' && <span className="badge badge-success badge-outline badge-sm">Saved</span>}
            </div>
            <p className="mt-1 text-sm text-base-content/60">
              {!recordingAvailable
                ? 'Manage its school-facing identity and scope here. Its content stays in the established workspace.'
                : domain.protected
                ? 'Its name and core identity stay consistent across Swan. Configure its school content in Event, Action and Rules.'
                : 'Name the area clearly and choose where staff begin when recording it.'}
            </p>
          </div>
          {canEditIdentity && active && (
            <button type="button" className="btn btn-outline btn-sm gap-2" onClick={() => setIsEditing(true)}>
              <PencilIcon className="h-4 w-4" /> Edit Domain
            </button>
          )}
          {!active && canReactivate && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={reactivate}
              disabled={availabilityStatus === 'saving'}
            >
              Reactivate Domain
            </button>
          )}
        </div>
        <p className="mt-3 text-xs text-base-content/50">
          {recordingAvailable ? (FIELD_TYPE_LABELS[primaryType] || 'Configured event') : 'Established workspace'}
          {' · '}{scope.length ? `Grades ${scope.join(', ')}` : 'All grades'}
        </p>
        {isAssessmentDomain(domain) && (
          <div className="mt-2 space-y-2">
            <p className="text-sm text-base-content/60">Programme setup defines each assessment&apos;s Points or Levels, status alternatives, total marks and contribution weights. Additional number questions stay separate from the assessment result. Results keep their context in Queue and Records.</p>
            <Link className="btn btn-outline btn-sm" to="/setup?area=academics&tab=programmes">Programme setup</Link>
          </div>
        )}
      </section>

      {active && recordingAvailable && <RecordingSettings key={`${schoolId}:${domain.id}`} domain={domain} schoolId={schoolId} />}
      {canConfigureDomain(domain, 'overview', 'edit', 'phaseApplicability') && <DomainPhaseSettings key={`phases:${schoolId}:${domain.id}`} domain={domain} schoolId={schoolId} />}
      {active && !recordingAvailable && (
        <section className="border-t border-base-200 pt-5">
          <h3 className="text-sm font-semibold">Recording is not used by this Domain</h3>
          <p className="mt-1 text-xs leading-relaxed text-base-content/55">
            This Domain Standard uses its established workspace rather than student Event forms, Quick Issue, Records or class display.
          </p>
          {domain.surfaces?.standalone?.route && (
            <Link className="btn btn-outline btn-sm mt-3" to={domain.surfaces.standalone.route}>
              Open {domain.label || 'workspace'}
            </Link>
          )}
        </section>
      )}

      {storiesAvailable && storiesConfigurable && (
        <section className="border-t border-base-200 pt-5">
          <label className="flex cursor-pointer items-start justify-between gap-4">
            <span>
              <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                Celebrate milestones with Stories
                {storiesStatus === 'saving' && <span className="badge badge-info badge-outline badge-sm">Saving…</span>}
                {storiesStatus === 'saved' && <span className="badge badge-success badge-outline badge-sm">Saved</span>}
                {storiesStatus === 'error' && <span className="badge badge-error badge-outline badge-sm">Save failed</span>}
              </span>
              <span className="mt-0.5 block text-xs text-base-content/55">
                Turn standout moments in this Domain into shareable cards for the board and parents.
              </span>
            </span>
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm shrink-0"
              checked={storiesOn}
              onChange={toggleStories}
              disabled={storiesSaving}
            />
          </label>
          {storiesStatus === 'error' && (
            <div className="alert alert-error mt-3 py-2 text-sm" role="alert">
              <span>{storiesError} Try the toggle again.</span>
            </div>
          )}
        </section>
      )}

      {storiesAvailable && !storiesConfigurable && (
        <section className="border-t border-base-200 pt-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium">Stories</h3>
              <p className="mt-0.5 text-xs text-base-content/55">Milestone Stories follow this Domain Standard’s shared Swan setting.</p>
            </div>
            <span className="badge badge-outline badge-sm">Swan default · Enabled</span>
          </div>
        </section>
      )}

      {canArchive && active && (
        <section className="border-t border-base-200 pt-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium">Sub-domains</h3>
              <p className="mt-0.5 text-xs text-base-content/55">Optional narrower areas within this Domain.</p>
            </div>
            {!showAddSub && (
              <button type="button" className="btn btn-ghost btn-sm gap-1" onClick={() => setShowAddSub(true)}>
                <PlusIcon className="h-4 w-4" /> Add
              </button>
            )}
          </div>
          {!showAddSub && (
            <p className="mt-3 text-sm text-base-content/55">
              Sub-domains appear as their own expandable cards directly beneath this Domain.
            </p>
          )}
          {showAddSub && (
              <DomainCreationForm
                schoolId={schoolId}
                userId={user?.uid}
                parentDomainId={domain.id}
                parentDomainLabel={domain.label || domain.name}
                onSuccess={(created) => {
                  setShowAddSub(false);
                  onCreated?.(created);
                }}
                onCancel={() => setShowAddSub(false)}
              />
          )}
        </section>
      )}

      {canArchive && active && (
        <div className="flex justify-end border-t border-base-200 pt-4">
          <button
            type="button"
            className="btn btn-ghost btn-sm gap-2 text-error"
            onClick={() => openArchive(domain, false)}
            disabled={busyArchiveId === domain.id}
          >
            {busyArchiveId === domain.id
              ? <span className="loading loading-spinner loading-xs" />
              : <ArchiveBoxArrowDownIcon className="h-4 w-4" />}
            Archive Domain
          </button>
        </div>
      )}

      {archiveTarget && (
        <ConfirmDialog
          isOpen
          onClose={() => setArchiveTarget(null)}
          onConfirm={archive}
          title={`Archive "${archiveTarget.entity.label}"?`}
          message={archiveTarget.count > 0
            ? `${archiveTarget.count.toLocaleString()} records will be hidden and remain recoverable through support.`
            : `"${archiveTarget.entity.label}" has no records yet.`}
          confirmText="Archive"
          variant={archiveTarget.count > 0 ? 'danger' : 'warning'}
          confirmTextMatch={archiveTarget.count > 0 ? archiveTarget.entity.label : null}
        />
      )}
    </div>
  );
}

export function DomainSettingsCard({ domain, schoolId, onArchived, onCreated, embedded = false }) {
  const [expanded, setExpanded] = useState(false);
  const body = (
    <DomainBody
      domain={domain}
      schoolId={schoolId}
      onArchived={onArchived}
      onCreated={onCreated}
    />
  );

  if (embedded) return body;

  return (
    <ExpandableCard
      flat
      expanded={expanded}
      onToggle={() => setExpanded((value) => !value)}
      header={(
        <div className="min-w-0">
          <span className="min-w-0">
            <span className="block truncate font-medium">{domain.label}</span>
            {domain.description && <span className="block truncate text-sm text-base-content/55">{domain.description}</span>}
          </span>
        </div>
      )}
      actions={['label', 'description', 'scope', 'terminology'].some((path) => (
        canConfigureDomain(domain, 'overview', 'edit', path)
      )) ? (
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square"
          aria-label="Edit domain"
          onClick={() => setExpanded(true)}
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      ) : null}
    >
      <div className="p-4 sm:p-5">{body}</div>
    </ExpandableCard>
  );
}

export default DomainSettingsCard;
